import { type FC, useCallback, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import Checkmark from 'dibs-icons/exports/legacy/Checkmark.ts';
import { WithWalletConnectionEnsuredModalBase } from './WithWalletConnectionEnsuredModalBase.tsx';
import { type UnsupportedBrowserContent_viewer$key } from './__generated__/UnsupportedBrowserContent_viewer.graphql.ts';

import styles from './styles/WithWalletConnectionEnsuredModalBase.scss';

export const UnsupportedBrowserContent: FC<{
    isMobile: boolean;
    viewer: UnsupportedBrowserContent_viewer$key;
}> = ({ isMobile, viewer: viewerRef }) => {
    useEffect(() => {
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'ethereum error displayed',
            label: 'unsupported browser',
        });
    }, []);

    const viewer = useFragment(
        graphql`
            fragment UnsupportedBrowserContent_viewer on Viewer {
                ...WithWalletConnectionEnsuredModalBase_viewer
            }
        `,
        viewerRef
    );

    const hasClipboardApi = !!navigator?.clipboard?.writeText;
    const [isCopied, setIsCopied] = useState(false);
    const copyToClipboard = useCallback(() => {
        try {
            navigator.clipboard.writeText(window.location.href).then(
                () => {
                    setIsCopied(true);
                },
                () => {
                    throw new Error();
                }
            );
        } catch (e) {
            // silently fail
        }
    }, []);
    return (
        <WithWalletConnectionEnsuredModalBase
            viewer={viewer}
            title={
                <FormattedMessage
                    id="nft.withWalletConnection.unsupportedBrowser.header"
                    defaultMessage="Your browser is not supported by MetaMask"
                />
            }
            paragraphs={[
                isMobile ? (
                    <>
                        <FormattedMessage
                            id="nft.withWalletConnection.unsupportedBrowser.bodyCopyMobile"
                            defaultMessage="In order to proceed, you must open this page in the MetaMask app's embedded browser."
                        />
                        {hasClipboardApi && (
                            <FormattedMessage
                                id="nft.withWalletConnection.unsupportedBrowser.bodyCopyMobileCopyLink"
                                defaultMessage=" Use the button below to copy the link page. Paste the link in the embedded browser to easily access it again."
                            />
                        )}
                    </>
                ) : (
                    <FormattedMessage
                        id="nft.withWalletConnection.unsupportedBrowser.bodyCopyDesktop"
                        defaultMessage="In order to proceed, you must open this page in Chrome, Firefox, Brave or Edge."
                    />
                ),
            ]}
            showWalletButtons={false}
        >
            {isMobile && hasClipboardApi && (
                <Button fullWidth onClick={copyToClipboard} className={styles.copyPdpLinkButton}>
                    {isCopied ? (
                        <>
                            <Checkmark className={styles.checkmarkIcon} />
                            <FormattedMessage
                                id="nft.withWalletConnection.unsupportedBrowser.copied"
                                defaultMessage="Copied"
                            />
                        </>
                    ) : (
                        <FormattedMessage
                            id="nft.withWalletConnection.unsupportedBrowser.copyLink"
                            defaultMessage="Copy Link"
                        />
                    )}
                </Button>
            )}
        </WithWalletConnectionEnsuredModalBase>
    );
};
